import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../lib/firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/auth-context';
import { motion } from 'framer-motion';
import { LLM_MODELS } from '../../shared/models';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    totalDebates: 0,
    activeProUsers: 0,
    totalRevenue: 0,
    debatesLastMonth: 0,
    newUsersLastMonth: 0,
    subscriptionConversionRate: 0,
    averageDebatesPerUser: 0,
    continuedDebatesCount: 0,
    canceledSubscriptions: 0,
    deletedDebatesCount: 0,
    debatesLastDay: 0,
    debatesLastWeek: 0,
    debatesLastYear: 0,
    usersLastDay: 0,
    usersLastWeek: 0,
    usersLastYear: 0,
    revenueLastDay: 0,
    revenueLastWeek: 0,
    revenueLastYear: 0,
    tokenUsageByModel: {
      day: {},
      week: {},
      month: {},
      lifetime: {}
    },
    totalTokenUsage: {
      day: { input: 0, output: 0, total: 0 },
      week: { input: 0, output: 0, total: 0 },
      month: { input: 0, output: 0, total: 0 },
      lifetime: { input: 0, output: 0, total: 0 }
    },
    topUsersByTokens: {
      day: [],
      week: [],
      month: [],
      lifetime: []
    }
  });
  const [error, setError] = useState('');

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        navigate('/login', { state: { from: '/admin' } });
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists() && userDoc.data().admin === true) {
          setIsAdmin(true);
          fetchMetrics();
        } else {
          setError('Unauthorized access');
          navigate('/');
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setError('Error checking permissions');
        navigate('/');
      }
    };

    checkAdminStatus();
  }, [user, navigate]);

  const fetchMetrics = async () => {
    try {
      const now = new Date();
      const oneDayAgo = new Date(now.getTime() - (24 * 60 * 60 * 1000));
      const oneWeekAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
      const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
      const oneYearAgo = new Date(now.getTime() - (365 * 24 * 60 * 60 * 1000));
      
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const users = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const debatesRef = collection(db, 'debates');
      const debatesSnapshot = await getDocs(debatesRef);
      const debates = debatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const debateDetailsRef = collection(db, 'debateDetails');
      const debateDetailsSnapshot = await getDocs(debateDetailsRef);
      const deletedDebatesCount = debateDetailsSnapshot.docs.filter(doc => 
        doc.data().status === 'deleted'
      ).length;

      const totalUsers = users.length;
      const totalDebates = debates.length;
      
      const activeProUsers = users.filter(user => 
        user.subscription && user.subscription.status === 'active'
      ).length;

      const totalRevenue = users.reduce((sum, user) => {
        if (!user.subscription?.payments) return sum;
        return sum + (Array.isArray(user.subscription.payments) 
          ? user.subscription.payments.reduce((total, payment) => 
              total + (payment?.amount || 0), 0)
          : 0
        );
      }, 0);

      const debatesLastDay = debates.filter(debate => {
        const createdAt = debate.createdAt?.toDate?.();
        return createdAt instanceof Date && createdAt > oneDayAgo;
      }).length;

      const debatesLastWeek = debates.filter(debate => {
        const createdAt = debate.createdAt?.toDate?.();
        return createdAt instanceof Date && createdAt > oneWeekAgo;
      }).length;

      const debatesLastMonth = debates.filter(debate => {
        const createdAt = debate.createdAt?.toDate?.();
        return createdAt instanceof Date && createdAt > thirtyDaysAgo;
      }).length;

      const debatesLastYear = debates.filter(debate => {
        const createdAt = debate.createdAt?.toDate?.();
        return createdAt instanceof Date && createdAt > oneYearAgo;
      }).length;

      const usersLastDay = users.filter(user => {
        const createdAt = user.createdAt?.toDate?.();
        return createdAt instanceof Date && createdAt > oneDayAgo;
      }).length;

      const usersLastWeek = users.filter(user => {
        const createdAt = user.createdAt?.toDate?.();
        return createdAt instanceof Date && createdAt > oneWeekAgo;
      }).length;

      const usersLastMonth = users.filter(user => {
        const createdAt = user.createdAt?.toDate?.();
        return createdAt instanceof Date && createdAt > thirtyDaysAgo;
      }).length;

      const usersLastYear = users.filter(user => {
        const createdAt = user.createdAt?.toDate?.();
        return createdAt instanceof Date && createdAt > oneYearAgo;
      }).length;

      const getRevenueInPeriod = (startDate) => users.reduce((sum, user) => {
        if (!user.subscription?.payments) return sum;
        return sum + (Array.isArray(user.subscription.payments) 
          ? user.subscription.payments.reduce((total, payment) => {
              const paymentDate = payment.created?.toDate?.();
              return (paymentDate instanceof Date && paymentDate > startDate) 
                ? total + (payment?.amount || 0)
                : total;
            }, 0)
          : 0
        );
      }, 0);

      const revenueLastDay = getRevenueInPeriod(oneDayAgo);
      const revenueLastWeek = getRevenueInPeriod(oneWeekAgo);
      const revenueLastYear = getRevenueInPeriod(oneYearAgo);

      const continuedDebatesCount = debates.filter(debate => 
        Array.isArray(debate.prompts) && debate.prompts.length > 1
      ).length;
      
      const canceledSubscriptions = users.filter(user => 
        user.subscription && user.subscription.status === 'canceled'
      ).length;

      const subscriptionConversionRate = totalUsers > 0 
        ? ((activeProUsers / totalUsers) * 100)
        : 0;
        
      const averageDebatesPerUser = totalUsers > 0 
        ? (totalDebates / totalUsers)
        : 0;

      const tokenMetrics = {
        byModel: {
          day: Object.fromEntries(Object.values(LLM_MODELS).map(model => [model, 0])),
          week: Object.fromEntries(Object.values(LLM_MODELS).map(model => [model, 0])),
          month: Object.fromEntries(Object.values(LLM_MODELS).map(model => [model, 0])),
          lifetime: Object.fromEntries(Object.values(LLM_MODELS).map(model => [model, 0]))
        },
        total: {
          day: { input: 0, output: 0, total: 0 },
          week: { input: 0, output: 0, total: 0 },
          month: { input: 0, output: 0, total: 0 },
          lifetime: { input: 0, output: 0, total: 0 }
        },
        topUsers: {
          day: [],
          week: [],
          month: [],
          lifetime: []
        }
      };

      // Process debates for token metrics
      debates.forEach(debate => {
        if (!debate.tokenCounts) return;

        const createdAt = debate.createdAt?.toDate();
        if (!createdAt) return;

        const model = debate.model || 'unknown';
        const { input = 0, output = 0, total = 0 } = debate.tokenCounts;

        // Always add to lifetime
        tokenMetrics.total.lifetime.input += input;
        tokenMetrics.total.lifetime.output += output;
        tokenMetrics.total.lifetime.total += total;
        tokenMetrics.byModel.lifetime[model] = (tokenMetrics.byModel.lifetime[model] || 0) + total;

        // Add to day metrics if within last 24 hours
        if (createdAt >= oneDayAgo) {
          tokenMetrics.total.day.input += input;
          tokenMetrics.total.day.output += output;
          tokenMetrics.total.day.total += total;
          tokenMetrics.byModel.day[model] = (tokenMetrics.byModel.day[model] || 0) + total;
        }

        // Add to week metrics if within last week
        if (createdAt >= oneWeekAgo) {
          tokenMetrics.total.week.input += input;
          tokenMetrics.total.week.output += output;
          tokenMetrics.total.week.total += total;
          tokenMetrics.byModel.week[model] = (tokenMetrics.byModel.week[model] || 0) + total;
        }

        // Add to month metrics if within last month
        if (createdAt >= thirtyDaysAgo) {
          tokenMetrics.total.month.input += input;
          tokenMetrics.total.month.output += output;
          tokenMetrics.total.month.total += total;
          tokenMetrics.byModel.month[model] = (tokenMetrics.byModel.month[model] || 0) + total;
        }
      });

      // Process user token data and get top users
      const userTokensByPeriod = users.reduce((acc, user) => {
        const userTokens = {
          id: user.id,
          email: user.email,
          tokenUsage: {
            day: 0,
            week: 0,
            month: 0,
            lifetime: user.lifetimeTokens?.total || 0
          }
        };

        // Add token usage from debates
        debates
          .filter(debate => debate.userId === user.id)
          .forEach(debate => {
            if (!debate.tokenCounts || !debate.createdAt) return;
            
            const createdAt = debate.createdAt.toDate();
            const total = debate.tokenCounts.total || 0;

            // Add to all applicable time periods
            if (createdAt >= oneDayAgo) {
              userTokens.tokenUsage.day += total;
            }
            if (createdAt >= oneWeekAgo) {
              userTokens.tokenUsage.week += total;
            }
            if (createdAt >= thirtyDaysAgo) {
              userTokens.tokenUsage.month += total;
            }
            userTokens.tokenUsage.lifetime += total;
          });

        acc.push(userTokens);
        return acc;
      }, []);

      // Get top users for each time period
      tokenMetrics.topUsers = {
        day: userTokensByPeriod
          .sort((a, b) => b.tokenUsage.day - a.tokenUsage.day)
          .slice(0, 5)
          .map(user => ({
            id: user.id,
            email: user.email,
            tokenUsage: user.tokenUsage.day
          })),
        week: userTokensByPeriod
          .sort((a, b) => b.tokenUsage.week - a.tokenUsage.week)
          .slice(0, 5)
          .map(user => ({
            id: user.id,
            email: user.email,
            tokenUsage: user.tokenUsage.week
          })),
        month: userTokensByPeriod
          .sort((a, b) => b.tokenUsage.month - a.tokenUsage.month)
          .slice(0, 5)
          .map(user => ({
            id: user.id,
            email: user.email,
            tokenUsage: user.tokenUsage.month
          })),
        lifetime: userTokensByPeriod
          .sort((a, b) => b.tokenUsage.lifetime - a.tokenUsage.lifetime)
          .slice(0, 5)
          .map(user => ({
            id: user.id,
            email: user.email,
            tokenUsage: user.tokenUsage.lifetime
          }))
      };

      // Update metrics state with all the data
      setMetrics(prevMetrics => ({
        ...prevMetrics,
        totalUsers,
        totalDebates,
        activeProUsers,
        totalRevenue,
        debatesLastMonth,
        newUsersLastMonth: usersLastMonth,
        subscriptionConversionRate,
        averageDebatesPerUser,
        continuedDebatesCount,
        canceledSubscriptions,
        deletedDebatesCount,
        debatesLastDay,
        debatesLastWeek,
        debatesLastYear,
        usersLastDay,
        usersLastWeek,
        usersLastYear,
        revenueLastDay,
        revenueLastWeek,
        revenueLastYear,
        tokenUsageByModel: tokenMetrics.byModel,
        totalTokenUsage: tokenMetrics.total,
        topUsersByTokens: tokenMetrics.topUsers
      }));

    } catch (error) {
      console.error('Error fetching metrics:', error);
      setError('Error fetching metrics: ' + error.message);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full space-y-8"
        >
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
              {error || 'Loading...'}
            </h2>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-8">Admin Dashboard</h1>
        
        <div className="space-y-12">
          {/* Current Metrics */}
          <div>
            <h2 className="text-xl font-semibold text-white mb-4">Current Metrics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard title="Total Users" value={metrics.totalUsers} />
              <MetricCard title="Pro Users" value={metrics.activeProUsers} />
              <MetricCard title="Total Debates" value={metrics.totalDebates} />
              <MetricCard title="Total Revenue" value={`$${(metrics.totalRevenue / 100).toFixed(2)}`} />
              <MetricCard title="Conversion Rate" value={`${metrics.subscriptionConversionRate.toFixed(1)}%`} />
              <MetricCard title="Avg Debates/User" value={metrics.averageDebatesPerUser.toFixed(1)} />
              <MetricCard title="Continued Debates" value={metrics.continuedDebatesCount} />
              <MetricCard title="Deleted Debates" value={metrics.deletedDebatesCount} />
              <MetricCard title="Canceled Subscriptions" value={metrics.canceledSubscriptions} />
            </div>
          </div>

          {/* Debate Activity */}
          <div>
            <h2 className="text-xl font-semibold text-white mb-4">Debate Activity</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <MetricCard title="Last 24 Hours" value={metrics.debatesLastDay} />
              <MetricCard title="Last Week" value={metrics.debatesLastWeek} />
              <MetricCard title="Last Month" value={metrics.debatesLastMonth} />
              <MetricCard title="Last Year" value={metrics.debatesLastYear} />
            </div>
          </div>

          {/* User Growth */}
          <div>
            <h2 className="text-xl font-semibold text-white mb-4">User Growth</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <MetricCard title="Last 24 Hours" value={metrics.usersLastDay} />
              <MetricCard title="Last Week" value={metrics.usersLastWeek} />
              <MetricCard title="Last Month" value={metrics.newUsersLastMonth} />
              <MetricCard title="Last Year" value={metrics.usersLastYear} />
            </div>
          </div>

          {/* Revenue */}
          <div>
            <h2 className="text-xl font-semibold text-white mb-4">Revenue</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <MetricCard title="Last 24 Hours" value={`$${(metrics.revenueLastDay / 100).toFixed(2)}`} />
              <MetricCard title="Last Week" value={`$${(metrics.revenueLastWeek / 100).toFixed(2)}`} />
              <MetricCard title="Last Month" value={`$${(metrics.revenueLastMonth / 100).toFixed(2)}`} />
              <MetricCard title="Last Year" value={`$${(metrics.revenueLastYear / 100).toFixed(2)}`} />
            </div>
          </div>

          {/* Total Token Usage */}
          <div>
            <h2 className="text-xl font-semibold text-white mb-4">Total Token Usage</h2>
            {['lifetime', 'month', 'week', 'day'].map(period => (
              <div key={period} className="mb-8">
                <h3 className="text-lg font-medium text-white mb-3 capitalize">{period} Stats</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <MetricCard 
                    title="Input Tokens"
                    value={metrics.totalTokenUsage[period].input.toLocaleString()}
                  />
                  <MetricCard 
                    title="Output Tokens"
                    value={metrics.totalTokenUsage[period].output.toLocaleString()}
                  />
                  <MetricCard 
                    title="Total Tokens"
                    value={metrics.totalTokenUsage[period].total.toLocaleString()}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Token Usage by Model */}
          <div>
            <h2 className="text-xl font-semibold text-white mb-4">Token Usage by Model</h2>
            {['lifetime', 'month', 'week', 'day'].map(period => (
              <div key={period} className="mb-8">
                <h3 className="text-lg font-medium text-white mb-3 capitalize">{period} Usage by Model</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  {Object.entries(metrics.tokenUsageByModel[period])
                    .sort(([_, a], [__, b]) => b - a)
                    .filter(([_, count]) => count > 0)
                    .map(([model, count]) => (
                      <MetricCard 
                        key={model}
                        title={model}
                        value={count.toLocaleString()}
                        subtitle={`${((count / metrics.totalTokenUsage[period].total) * 100).toFixed(1)}% of ${period}`}
                      />
                    ))}
                </div>
              </div>
            ))}
          </div>

          {/* Top Users */}
          <div>
            <h2 className="text-xl font-semibold text-white mb-4">Top Users by Token Usage</h2>
            {['lifetime', 'month', 'week', 'day'].map(period => (
              <div key={period} className="mb-8">
                <h3 className="text-lg font-medium text-white mb-3 capitalize">{period} Top Users</h3>
                <div className="bg-gray-800 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Total Tokens</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">% of {period}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {metrics.topUsersByTokens[period].map((user) => (
                        <tr key={user.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            {user.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            {user.tokenUsage.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            {((user.tokenUsage / metrics.totalTokenUsage[period].total) * 100).toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({ title, value, subtitle }) => (
  <div className="bg-gray-800 rounded-lg p-6">
    <h3 className="text-gray-400 text-sm font-medium mb-2">{title}</h3>
    <p className="text-white text-2xl font-semibold">{value}</p>
    {subtitle && <p className="text-gray-400 text-sm mt-1">{subtitle}</p>}
  </div>
);

export default AdminDashboard;